import * as React from 'react'

import { Api, PartialArticle } from '@doist/help-center-utils'
import { Box, CloseButton, Hairline, Icons, SearchField } from '@doist/marketist'
import { AutocompleteSearchResult } from '@doist/marketist/dist/components/AutocompleteSearch/AutocompleteSearch'

import HtmlToReact from 'html-react-parser'
import debounce from 'lodash/debounce'
import { useTranslation } from 'next-i18next'

import { SearchResult } from './SearchResult/SearchResult'

import css from './MobileSearch.module.css'

export type MobileSearchProps = {
    title: string
    language: string
}

export function MobileSearch({ title, language }: MobileSearchProps) {
    const { t } = useTranslation('common')
    const [open, setOpen] = React.useState(false)
    const [searchText, setSearchText] = React.useState('')
    const [searchResults, setSearchResults] = React.useState<AutocompleteSearchResult[]>([])
    const [searchStarted, setSearchStarted] = React.useState(false)

    async function doSearch(searchString: string) {
        if (!searchString || searchString.length === 0) {
            setSearchResults([])
            return
        }

        try {
            const api = new Api(process.env.ZENDESK_API_URL, '', language)
            const results = await api.searchArticles(searchString, language)
            /** Shows only the first 4 results. */
            const normResults: AutocompleteSearchResult[] = results
                .slice(0, 4)
                .map((result: PartialArticle) => ({
                    id: `${result.id}`,
                    title: result.title,
                    href: result.href,
                    description: HtmlToReact(result.snippet ?? ''),
                }))
            setSearchResults(normResults)
        } catch {
            setSearchResults([])
        }
    }
    const debouncedSearch = React.useRef(debounce(doSearch, 500))

    React.useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : 'auto'
        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [open])

    return (
        <Box className={css.mobileSearch}>
            <button onClick={() => setOpen(true)}>
                <Icons.Search width={19} height={19} />
            </button>

            {open && (
                <Box className={css.searchContent}>
                    <Box className={css.backgroundOverlay} onClick={() => setOpen(false)} />

                    <Box className={css.searchContainer}>
                        <Box className={css.close}>
                            <CloseButton onClick={() => setOpen(false)} />
                        </Box>

                        <SearchField
                            id="article-search"
                            label={title}
                            placeholder={title}
                            searchLabel={t('search')}
                            clearLabel={t('clearSearch')}
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.currentTarget.value)
                                debouncedSearch.current(e.currentTarget.value)
                            }}
                            onSearch={(query) => {
                                setSearchStarted(true)
                                location.href = '/help/search?query=' + encodeURIComponent(query)
                            }}
                            disabled={searchStarted}
                        />

                        {searchResults.length > 0 && (
                            <>
                                <Hairline />

                                <ul>
                                    {searchResults.map((result) => (
                                        <li key={`result-${result.id}`}>
                                            <SearchResult searchResult={result} />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}
